/**
 * Web component: Doogma add to cart (doogma-add-to-cart)
 * -----------------------------------------------------------------------------
 * Handles payload from Doogma.
 *
 */

/**
 * Create new <doogma-add-to-cart> custom element.
 */
class DoogmaAddToCart extends HTMLElement {

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super()
    this.bundle = {}
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer')
    this.cartUrl = this.dataset.cartUrl
  }

  /**
   * Lifecycle hook - component is attached to the DOM.
   */
  connectedCallback() {
    document.addEventListener('doogmaAddToBag', this.handleEvent.bind(this))
  }

  /**
   * Handle Doogma event.
   */
  async handleEvent() {
    this.bundle = window.doogmaBundle
    const engravingProductProperty = this.dataset.engravingProduct

    /**
     * Find parent ID based on entry which isn't an object.
     * - Even if Doogma returns no objects this will find first item.
     */
    const parentId = this.bundle?.variantIds.find((entry) => {
      return typeof entry !== 'object'
    })

    /**
     * Find if any items are objects (new charms with engraving).
     */
    const hasObjects = this.bundle?.variantIds.some((entry) => {
      return typeof entry === 'object'
    })

    /**
     * Build list of items based on variantIds array.
     * - Flatten as engraving charms return an array of product and fee.
     */
    const items = this.bundle?.variantIds
      .map((entry, index) => {

        /**
         * If the entry isn't an object then it's the parent.
         * - Doogma returns only a number for the first (parent) item.
         * - If Doogma returns no objects then use first item as parent.
         */
        if (
          (hasObjects && typeof entry !== 'object') ||
          (!hasObjects && index === 0)
        ) {
          return {
            id: entry,
            properties: {
              _doogmaSavedDesignId: this.bundle.doogmaSavedDesignId,
              _doogmaThumb: this.bundle.doogmaThumb,
              _group_parent: this.dataset.groupType
            },
            quantity: 1,
          }
        }

        /**
         * If no objects then just return simple object.
         */
        if (!hasObjects) {
          return {
            id: entry,
            parent_id: parentId,
            properties: {
              _doogmaSavedDesignId: this.bundle.doogmaSavedDesignId,
            },
            quantity: 1,
          }
        }

        /**
         * Otherwise it's a charm.
         * - First setup the object.
         */
        const item = {
          id: entry.id,
          parent_id: parentId,
          quantity: 1,
        }

        /**
         * If engraving isn't an object then no engraving.
         */
        if (typeof entry.engraving !== 'object') {
          return item
        }

        /**
         * Build engraving input properties.
         */
        const inputProperties = this.buildEngravingInputProperties(entry.id)

        /**
         * Build engraving output properties.
         */
        let frontOutputProperties = {}
        let backOutputProperties = {}

        if (entry.engraving?.text) {
          frontOutputProperties = this.buildEngravingOutputProperties(entry.engraving, 'front')

        } else {
          if (entry.engraving?.front?.text) {
            frontOutputProperties = this.buildEngravingOutputProperties(entry.engraving.front, 'front')
          }

          if (entry.engraving?.back?.text) {
            backOutputProperties = this.buildEngravingOutputProperties(entry.engraving.back, 'back')
          }
        }

        const properties = {
          _doogmaSavedDesignId: this.bundle.doogmaSavedDesignId,
          ...inputProperties,
          ...frontOutputProperties,
          ...backOutputProperties,
        }

        item.properties = properties

        /**
         * If no engraving fee product then just return charm.
         */
        if (!engravingProductProperty) {
          return item
        }

        /**
         * Otherwise, add engraving fee product with same properties.
         */
        const items = [
          item,
          {
            id: Number(engravingProductProperty),
            parent_id: parentId,
            properties: {
              ...properties,
              _doogma_engraving_fee: true,
            },
            quantity: 1,
          }
        ]

        return items
      })
      .flat()

    console.log('items', items)

    try {
      await this.addToCart(items)
    } catch (error) {
      console.error('Failed to add to cart', error)
    }
  }

  /**
   * Build engraving input properties.
   * @param {Number} id - Charm ID.
   * @returns {Object}
   */
  buildEngravingInputProperties(id) {
    const includedInputKeys = ['backEngraving', 'boundingHeight', 'boundingWidth']
    const properties = {}

    /**
     * Find ID in charm data to populate missing metafield properties.
     */
    const matchedCharm = window.doogmaCharms.find((charm) => {
      return id === charm.variantId
    })

    if (!matchedCharm) {
      return properties
    }

    /**
     * Add input values to properties.
     */
    Object.entries(matchedCharm?.engraving).forEach(([key, value]) => {
      if (!includedInputKeys.includes(key)) {
        return
      }

      properties[`_doogma_engraving_${key}`] = value
    })

    return properties
  }

  /**
   * Build engraving output properties.
   * @param {Object} output - Engraving object output.
   * @param {String} position - Front or back.
   * @returns {Object}
   */
  buildEngravingOutputProperties(output, position) {
    const excludedOutputKeys = ['fontSize', 'sku']
    const properties = {}

    /**
     * Add output values to properties.
     */
    Object.entries(output).forEach(([key, value]) => {
      if (excludedOutputKeys.includes(key)) {
        return
      }

      properties[`_doogma_engraving_${position}_${key}`] = value
    })

    return properties
  }

  /**
   * Handle add to cart.
   * @param {Array} items - Items to add.
   * @returns {Promise}
   */
  addToCart(items) {
    return new Promise(async(resolve, reject) => {
      try {
        const body = {
          items,
        }

        /**
         * Add sections to render to trigger cart drawer.
         */
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id)
          body.sections_url = window.location.pathname
        }

        /**
         * Add to cart.
         */
        let response = await fetch(this.cartUrl, {
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        })

        const responseOk = response.ok
        response = await response.json()

        if (!responseOk) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: items[0]?.id,
            errors: response.errors || response.description,
            message: response.message,
          })

          reject(response)
          return
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: items[0]?.id,
          cartData: response,
        })

        this.cart.renderContents(response)

        if (this.cart && this.cart.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty')
        }

        resolve(response)

      } catch (error) {
        reject(error?.description || error)
      }
    })
  }
}

customElements.define('doogma-add-to-cart', DoogmaAddToCart)
